import type { VillageLayout, Structure, PathSegment, FencePost, StructureType, Prop } from './types'
import { STRUCTURE_DIMS } from './types'
import { heightAt } from '../terrain'

function mulberry32(seed: number) {
  let s = seed | 0
  return () => {
    s = (s + 0x6D2B79F5) | 0
    let t = Math.imul(s ^ (s >>> 15), 1 | s)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

const VILLAGE_CONFIGS = {
  small: {
    houses: 8,
    extras: ['trading_post', 'pub', 'stable', 'barn', 'armory'] as StructureType[],
    roadLength: 350,
    roadBranches: 2,
    pastures: 1,
    farms: 1,
  },
  medium: {
    houses: 14,
    extras: ['trading_post', 'pub', 'stable', 'barn', 'church', 'armory', 'barn'] as StructureType[],
    roadLength: 500,
    roadBranches: 3,
    pastures: 2,
    farms: 2,
  },
  large: {
    houses: 22,
    extras: ['trading_post', 'pub', 'stable', 'barn', 'church', 'armory', 'barn', 'barn'] as StructureType[],
    roadLength: 700,
    roadBranches: 4,
    pastures: 3,
    farms: 3,
  },
}

interface RoadNode {
  x: number
  z: number
  isMain: boolean
}

/**
 * Generate a branching road network radiating from center.
 * Returns road segments and nodes where buildings can be placed.
 */
function generateRoads(
  rng: () => number,
  roadLength: number,
  branches: number,
): {
  segments: PathSegment[]
  buildingSlots: Array<{ x: number; z: number; facing: number; isMain: boolean }>
  mainNodes: RoadNode[]
} {
  const segments: PathSegment[] = []
  const buildingSlots: Array<{ x: number; z: number; facing: number; isMain: boolean }> = []

  // Main road: a gently curving line through the center
  const mainAngle = rng() * Math.PI * 2
  const mainNodes: RoadNode[] = []
  const STEP = 15
  const numSteps = Math.floor(roadLength / STEP)

  let cx = 0, cz = 0, angle = mainAngle
  for (let i = 0; i <= numSteps; i++) {
    mainNodes.push({ x: cx, z: cz, isMain: true })
    // Gentle random curvature
    angle += (rng() - 0.5) * 0.4
    cx += Math.cos(angle) * STEP
    cz += Math.sin(angle) * STEP
  }

  // Center the road around origin
  const midIdx = Math.floor(mainNodes.length / 2)
  const ox = mainNodes[midIdx].x, oz = mainNodes[midIdx].z
  for (const n of mainNodes) { n.x -= ox; n.z -= oz }

  // Add main road segments
  for (let i = 0; i < mainNodes.length - 1; i++) {
    segments.push({
      fromX: mainNodes[i].x, fromZ: mainNodes[i].z,
      toX: mainNodes[i + 1].x, toZ: mainNodes[i + 1].z,
    })
  }

  // Building slots along main road (both sides at every node)
  for (let i = 1; i < mainNodes.length - 1; i++) {
    const dx = mainNodes[i + 1].x - mainNodes[i - 1].x
    const dz = mainNodes[i + 1].z - mainNodes[i - 1].z
    const len = Math.sqrt(dx * dx + dz * dz)
    if (len === 0) continue
    const nx = -dz / len, nz = dx / len // perpendicular
    const facing = Math.atan2(-nx, -nz) // face toward road

    const distL = 8 + rng() * 6
    const distR = 8 + rng() * 6
    buildingSlots.push({ x: mainNodes[i].x + nx * distL, z: mainNodes[i].z + nz * distL, facing, isMain: true })
    buildingSlots.push({ x: mainNodes[i].x - nx * distR, z: mainNodes[i].z - nz * distR, facing: facing + Math.PI, isMain: true })
  }

  // Branch roads
  for (let b = 0; b < branches; b++) {
    // Pick a point along the main road to branch from
    const branchIdx = 2 + Math.floor(rng() * (mainNodes.length - 4))
    const branchFrom = mainNodes[branchIdx]

    // Branch perpendicular-ish to main road
    const mdx = mainNodes[branchIdx + 1].x - mainNodes[branchIdx - 1].x
    const mdz = mainNodes[branchIdx + 1].z - mainNodes[branchIdx - 1].z
    const mlen = Math.sqrt(mdx * mdx + mdz * mdz)
    let branchAngle = Math.atan2(mdz / mlen, mdx / mlen) + (rng() > 0.5 ? 1 : -1) * (Math.PI / 2 + (rng() - 0.5) * 0.5)

    const branchLen = 120 + rng() * 150
    const branchSteps = Math.floor(branchLen / STEP)
    let bx = branchFrom.x, bz = branchFrom.z
    const branchNodes: RoadNode[] = [{ x: bx, z: bz, isMain: false }]

    for (let i = 0; i < branchSteps; i++) {
      branchAngle += (rng() - 0.5) * 0.3
      bx += Math.cos(branchAngle) * STEP
      bz += Math.sin(branchAngle) * STEP
      branchNodes.push({ x: bx, z: bz, isMain: false })
    }

    for (let i = 0; i < branchNodes.length - 1; i++) {
      segments.push({
        fromX: branchNodes[i].x, fromZ: branchNodes[i].z,
        toX: branchNodes[i + 1].x, toZ: branchNodes[i + 1].z,
      })
    }

    // Building slots along branch (both sides)
    for (let i = 1; i < branchNodes.length; i++) {
      const pdx = branchNodes[i].x - branchNodes[i - 1].x
      const pdz = branchNodes[i].z - branchNodes[i - 1].z
      const plen = Math.sqrt(pdx * pdx + pdz * pdz)
      if (plen === 0) continue
      const pnx = -pdz / plen, pnz = pdx / plen
      const facing = Math.atan2(-pnx, -pnz)
      const distL = 8 + rng() * 6
      const distR = 8 + rng() * 6
      buildingSlots.push({ x: branchNodes[i].x + pnx * distL, z: branchNodes[i].z + pnz * distL, facing, isMain: false })
      buildingSlots.push({ x: branchNodes[i].x - pnx * distR, z: branchNodes[i].z - pnz * distR, facing: facing + Math.PI, isMain: false })
    }
  }

  return { segments, buildingSlots, mainNodes }
}

/**
 * Compute convex hull of 2D points using monotone chain algorithm.
 */
function convexHull(points: Array<{ x: number; z: number }>): Array<{ x: number; z: number }> {
  const pts = [...points].sort((a, b) => a.x - b.x || a.z - b.z)
  if (pts.length <= 2) return pts

  const cross = (o: { x: number; z: number }, a: { x: number; z: number }, b: { x: number; z: number }) =>
    (a.x - o.x) * (b.z - o.z) - (a.z - o.z) * (b.x - o.x)

  // Lower hull
  const lower: Array<{ x: number; z: number }> = []
  for (const p of pts) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop()
    lower.push(p)
  }

  // Upper hull
  const upper: Array<{ x: number; z: number }> = []
  for (let i = pts.length - 1; i >= 0; i--) {
    const p = pts[i]
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop()
    upper.push(p)
  }

  // Remove last point of each half (it's the first point of the other)
  lower.pop()
  upper.pop()
  return lower.concat(upper)
}

/**
 * Point-in-convex-polygon test (CCW winding).
 */
function insideHull(px: number, pz: number, poly: Array<{ x: number; z: number }>): boolean {
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i], b = poly[(i + 1) % poly.length]
    const cross = (b.x - a.x) * (pz - a.z) - (b.z - a.z) * (px - a.x)
    if (cross < 0) return false
  }
  return true
}

/**
 * Check if a candidate position overlaps any already-placed footprint.
 * gap = minimum clearance in meters.
 */
function overlapsAny(
  cx: number, cz: number, w: number, d: number,
  placed: Array<{ x: number; z: number; w: number; d: number }>,
  gap: number,
): boolean {
  for (const p of placed) {
    const gapX = Math.abs(cx - p.x) - (w + p.w) / 2 - gap
    const gapZ = Math.abs(cz - p.z) - (d + p.d) / 2 - gap
    if (gapX < 0 && gapZ < 0) return true
  }
  return false
}

/** Optional village world info for terrain-aware placement of outlying structures */
interface VillageWorldInfo {
  nx: number; ny: number; nz: number  // unit normal of village center
  tangentX: { x: number; y: number; z: number }
  tangentZ: { x: number; y: number; z: number }
}

/** Convert local 2D offset to world normal direction for heightAt checks */
function localToNormal(info: VillageWorldInfo, lx: number, lz: number): { nx: number; ny: number; nz: number } {
  const wx = info.nx + info.tangentX.x * lx * 0.0001 + info.tangentZ.x * lz * 0.0001
  const wy = info.ny + info.tangentX.y * lx * 0.0001 + info.tangentZ.y * lz * 0.0001
  const wz = info.nz + info.tangentX.z * lx * 0.0001 + info.tangentZ.z * lz * 0.0001
  const len = Math.sqrt(wx * wx + wy * wy + wz * wz)
  return { nx: wx / len, ny: wy / len, nz: wz / len }
}

export function generateLayout(size: 'small' | 'medium' | 'large', seed: number, worldInfo?: VillageWorldInfo): VillageLayout {
  const rng = mulberry32(seed)
  const config = VILLAGE_CONFIGS[size]

  // 1. Generate road network
  const { segments: paths, buildingSlots, mainNodes } = generateRoads(rng, config.roadLength, config.roadBranches)

  // 2. Build the structure list
  const structures: Structure[] = []
  const usedSlots = new Set<number>()
  const placed: Array<{ x: number; z: number; w: number; d: number }> = []

  // --- Castle at center (slightly offset) ---
  const castleX = (rng() - 0.5) * 6
  const castleZ = (rng() - 0.5) * 6
  structures.push({
    type: 'castle',
    localX: castleX,
    localZ: castleZ,
    rotation: rng() * Math.PI * 2,
  })
  const castleDims = STRUCTURE_DIMS.castle
  const castleMax = Math.max(castleDims[0], castleDims[2])
  placed.push({ x: castleX, z: castleZ, w: castleMax, d: castleMax })

  // ---------------------------------------------------------------------------
  //  HELPER: place a building in a slot matching a filter, with overlap checking
  // ---------------------------------------------------------------------------
  function placeInSlot(
    type: StructureType,
    filter: (slot: typeof buildingSlots[0], idx: number) => number, // scoring fn, higher = better; -1 = skip
    rotationOverride?: number,
  ): Structure | null {
    const dims = STRUCTURE_DIMS[type]
    let bestSlotIdx = -1
    let bestScore = -Infinity

    for (let si = 0; si < buildingSlots.length; si++) {
      if (usedSlots.has(si)) continue
      const slot = buildingSlots[si]
      const score = filter(slot, si)
      if (score < 0) continue
      const slotMax = Math.max(dims[0], dims[2])
      if (overlapsAny(slot.x, slot.z, slotMax, slotMax, placed, 5)) continue
      if (score > bestScore) {
        bestScore = score
        bestSlotIdx = si
      }
    }

    if (bestSlotIdx === -1) return null

    usedSlots.add(bestSlotIdx)
    const slot = buildingSlots[bestSlotIdx]
    const jx = (rng() - 0.5) * 3
    const jz = (rng() - 0.5) * 3
    // Elongated buildings (depth > width * 1.3) rotate +PI/2 so long wall faces road
    const dims2 = STRUCTURE_DIMS[type]
    const elongated = dims2[2] > dims2[0] * 1.3
    const baseRot = rotationOverride !== undefined ? rotationOverride : slot.facing + (elongated ? Math.PI / 2 : 0) + (rng() - 0.5) * 0.15
    const rot = baseRot
    const s: Structure = { type, localX: slot.x + jx, localZ: slot.z + jz, rotation: rot }
    structures.push(s)
    const maxDim = Math.max(dims[0], dims[2])
    placed.push({ x: s.localX, z: s.localZ, w: maxDim, d: maxDim })
    return s
  }

  /**
   * Fallback: random placement when no slot is suitable.
   */
  function placeFallback(type: StructureType): Structure {
    const dims = STRUCTURE_DIMS[type]
    const maxDim = Math.max(dims[0], dims[2])
    // Try up to 30 random positions with overlap checking
    for (let attempt = 0; attempt < 30; attempt++) {
      const angle = rng() * Math.PI * 2
      const dist = config.roadLength * 0.3 + rng() * config.roadLength * 0.3
      const lx = Math.cos(angle) * dist
      const lz = Math.sin(angle) * dist
      if (!overlapsAny(lx, lz, maxDim, maxDim, placed, 5)) {
        const s: Structure = { type, localX: lx, localZ: lz, rotation: rng() * Math.PI * 2 }
        structures.push(s)
        placed.push({ x: s.localX, z: s.localZ, w: maxDim, d: maxDim })
        return s
      }
    }
    // Last resort: place anyway (shouldn't happen often)
    const angle = rng() * Math.PI * 2
    const dist = config.roadLength * 0.4 + rng() * config.roadLength * 0.4
    const s: Structure = {
      type,
      localX: Math.cos(angle) * dist,
      localZ: Math.sin(angle) * dist,
      rotation: rng() * Math.PI * 2,
    }
    structures.push(s)
    placed.push({ x: s.localX, z: s.localZ, w: maxDim, d: maxDim })
    return s
  }

  // ---------------------------------------------------------------------------
  //  PLACE SPECIAL BUILDINGS (order matters — historically accurate placement)
  // ---------------------------------------------------------------------------

  // --- Church: near center but NOT at center, east-west orientation ---
  const hasChurch = config.extras.includes('church')
  if (hasChurch) {
    // Orient east-west: rotation 0 makes depth axis point along +Z, so PI/2 or -PI/2 makes it E-W
    // Actually rotation 0 or PI aligns depth along local Z. For east-west we want ~0 or ~PI.
    const churchRot = rng() > 0.5 ? 0 : Math.PI
    const result = placeInSlot('church', (slot) => {
      const dist = Math.sqrt(slot.x * slot.x + slot.z * slot.z)
      // Prefer near center (within 30-80m) but not right at center (castle is there)
      if (dist < 20) return -1
      if (dist > 120) return -1
      return 100 - dist // closer to center = better
    }, churchRot)
    if (!result) placeFallback('church')
  }

  // --- Trading Post: central, on or near main road ---
  {
    const result = placeInSlot('trading_post', (slot) => {
      if (!slot.isMain) return -1 // must be on main road
      const dist = Math.sqrt(slot.x * slot.x + slot.z * slot.z)
      if (dist > 100) return -1
      return 80 - dist
    })
    if (!result) placeFallback('trading_post')
  }

  // --- Pub/Tavern: ON the main road, near center ---
  {
    const result = placeInSlot('pub', (slot) => {
      if (!slot.isMain) return -1 // must be on main road
      const dist = Math.sqrt(slot.x * slot.x + slot.z * slot.z)
      if (dist > 120) return -1
      return 90 - dist
    })
    if (!result) placeFallback('pub')
  }

  // --- Stable: near village edge, on or near main road (travelers need access) ---
  {
    const result = placeInSlot('stable', (slot) => {
      const dist = Math.sqrt(slot.x * slot.x + slot.z * slot.z)
      // Prefer farther from center but still on main road
      if (!slot.isMain) return dist * 0.3 // branch roads OK but less preferred
      return dist // farther = better for stable
    })
    if (!result) placeFallback('stable')
  }

  // --- Armory/Smithy: at the EDGE of the village, far from center, near a road ---
  if (config.extras.includes('armory')) {
    const result = placeInSlot('armory', (slot) => {
      const dist = Math.sqrt(slot.x * slot.x + slot.z * slot.z)
      // Strongly prefer far from center (downwind edge)
      return dist * 2
    })
    if (!result) placeFallback('armory')
  }

  // --- Houses: distributed evenly along roads, facing the road ---
  const houseSlotsNeeded = config.houses
  // Collect remaining available slot indices
  const availableSlotIndices: number[] = []
  for (let si = 0; si < buildingSlots.length; si++) {
    if (!usedSlots.has(si)) availableSlotIndices.push(si)
  }

  // Even distribution: pick every Nth available slot
  const stride = Math.max(1, Math.floor(availableSlotIndices.length / (houseSlotsNeeded + 1)))
  const housesPlaced: Structure[] = []
  let houseIdx = 0

  for (let h = 0; h < houseSlotsNeeded; h++) {
    const dims = STRUCTURE_DIMS.house
    let placed_house = false

    // Try slots at regular intervals
    for (let offset = 0; offset < availableSlotIndices.length; offset++) {
      const targetRaw = houseIdx * stride + offset
      const si = availableSlotIndices[targetRaw % availableSlotIndices.length]
      if (usedSlots.has(si)) continue
      const slot = buildingSlots[si]
      const houseMaxDim = Math.max(dims[0], dims[2])
      if (overlapsAny(slot.x, slot.z, houseMaxDim, houseMaxDim, placed, 5)) continue

      usedSlots.add(si)
      const jx = (rng() - 0.5) * 3
      const jz = (rng() - 0.5) * 3
      const s: Structure = {
        type: 'house',
        localX: slot.x + jx,
        localZ: slot.z + jz,
        rotation: slot.facing + Math.PI / 2 + (rng() - 0.5) * 0.15, // long wall faces road
      }
      structures.push(s)
      const houseMax = Math.max(dims[0], dims[2])
      placed.push({ x: s.localX, z: s.localZ, w: houseMax, d: houseMax })
      housesPlaced.push(s)
      placed_house = true
      houseIdx++
      break
    }

    if (!placed_house) {
      const fb = placeFallback('house')
      housesPlaced.push(fb)
      houseIdx++
    }
  }

  // --- Barns: place adjacent to a house (within 15m of nearest house) ---
  const barnCount = config.extras.filter(t => t === 'barn').length
  for (let bi = 0; bi < barnCount; bi++) {
    // Find a house to attach this barn to; prefer houses that don't already have a barn nearby
    const result = placeInSlot('barn', (slot) => {
      // Must be near a house
      let nearestHouseDist = Infinity
      for (const h of housesPlaced) {
        const d = Math.sqrt((slot.x - h.localX) ** 2 + (slot.z - h.localZ) ** 2)
        if (d < nearestHouseDist) nearestHouseDist = d
      }
      if (nearestHouseDist > 25) return -1 // too far from any house
      // Prefer closer to a house
      return 30 - nearestHouseDist
    })
    if (!result) placeFallback('barn')
  }

  // 3. Generate organic fence around the village using convex hull + padding + noise
  const allPoints = structures.map(s => ({ x: s.localX, z: s.localZ }))
  const hull = convexHull(allPoints)

  const FENCE_PADDING = 20
  const fencePosts: FencePost[] = []

  // Compute centroid
  let centX = 0, centZ = 0
  for (const p of hull) { centX += p.x; centZ += p.z }
  centX /= hull.length; centZ /= hull.length

  // Find where roads exit the fence perimeter
  const roadExits: Array<{ x: number; z: number }> = []
  const GATE_WIDTH = 6
  for (const seg of paths) {
    for (const [ex, ez] of [[seg.fromX, seg.fromZ], [seg.toX, seg.toZ]]) {
      const d = Math.sqrt((ex - centX) ** 2 + (ez - centZ) ** 2)
      let maxHullDist = 0
      for (const hp of hull) {
        const hd = Math.sqrt((hp.x - centX) ** 2 + (hp.z - centZ) ** 2)
        if (hd > maxHullDist) maxHullDist = hd
      }
      if (d > maxHullDist * 0.6) {
        roadExits.push({ x: ex, z: ez })
      }
    }
  }

  // Subdivide hull edges and push outward, skip posts near road exits
  const POST_SPACING = 4
  for (let i = 0; i < hull.length; i++) {
    const a = hull[i]
    const b = hull[(i + 1) % hull.length]
    const edgeLen = Math.sqrt((b.x - a.x) ** 2 + (b.z - a.z) ** 2)
    const numSub = Math.max(1, Math.floor(edgeLen / POST_SPACING))

    for (let j = 0; j < numSub; j++) {
      const t = j / numSub
      const px = a.x + (b.x - a.x) * t
      const pz = a.z + (b.z - a.z) * t

      const dx = px - centX, dz = pz - centZ
      const dlen = Math.sqrt(dx * dx + dz * dz)
      const nx = dx / dlen, nz = dz / dlen

      const noiseVal = Math.sin(i * 3.7 + j * 1.3 + seed * 0.01) * 0.4 +
                       Math.sin(i * 7.1 + j * 2.9 + seed * 0.03) * 0.3
      const padding = FENCE_PADDING + noiseVal * 8

      const postX = px + nx * padding
      const postZ = pz + nz * padding

      let nearRoad = false
      for (const exit of roadExits) {
        const rd = Math.sqrt((postX - exit.x) ** 2 + (postZ - exit.z) ** 2)
        if (rd < GATE_WIDTH + FENCE_PADDING) { nearRoad = true; break }
      }
      if (nearRoad) continue

      fencePosts.push({ localX: postX, localZ: postZ })
    }
  }

  // Compute fence radius
  let maxR = 0
  for (const fp of fencePosts) {
    const d = Math.sqrt((fp.localX - centX) ** 2 + (fp.localZ - centZ) ** 2)
    if (d > maxR) maxR = d
  }

  // 4. Place pastures inside the village fence
  const pastureDims = STRUCTURE_DIMS.pasture
  const pastureR = Math.max(pastureDims[0], pastureDims[2]) / 2

  // Create the padded hull polygon
  const paddedHull: Array<{ x: number; z: number }> = []
  for (const hp of hull) {
    const dx = hp.x - centX, dz = hp.z - centZ
    const dlen = Math.sqrt(dx * dx + dz * dz)
    if (dlen === 0) continue
    paddedHull.push({
      x: hp.x + (dx / dlen) * FENCE_PADDING,
      z: hp.z + (dz / dlen) * FENCE_PADDING,
    })
  }

  // Inset the padded hull by pastureR
  const insetHull: Array<{ x: number; z: number }> = []
  for (const hp of paddedHull) {
    const dx = hp.x - centX, dz = hp.z - centZ
    const dlen = Math.sqrt(dx * dx + dz * dz)
    if (dlen === 0) continue
    const shrink = Math.min(pastureR + 5, dlen * 0.5)
    insetHull.push({
      x: hp.x - (dx / dlen) * shrink,
      z: hp.z - (dz / dlen) * shrink,
    })
  }

  // Check hull winding
  if (!insideHull(centX, centZ, insetHull)) {
    insetHull.reverse()
  }

  // Dense road sampling for distance checks
  const roadPoints: Array<{ x: number; z: number }> = []
  for (const seg of paths) {
    const segLen = Math.sqrt((seg.toX - seg.fromX) ** 2 + (seg.toZ - seg.fromZ) ** 2)
    const steps = Math.max(3, Math.floor(segLen / 5))
    for (let s = 0; s <= steps; s++) {
      const t = s / steps
      roadPoints.push({
        x: seg.fromX + (seg.toX - seg.fromX) * t,
        z: seg.fromZ + (seg.toZ - seg.fromZ) * t,
      })
    }
  }

  const pasturesPlaced: Array<{ x: number; z: number }> = []

  for (let pi = 0; pi < config.pastures; pi++) {
    let bestX = 0, bestZ = 0, bestScore = -1

    for (let attempt = 0; attempt < 200; attempt++) {
      const angle = rng() * Math.PI * 2
      const dist = 15 + rng() * (maxR * 0.7)
      const cx = centX + Math.cos(angle) * dist
      const cz = centZ + Math.sin(angle) * dist

      if (!insideHull(cx, cz, insetHull)) continue

      let minRoadDist = Infinity
      for (const rp of roadPoints) {
        const d = Math.sqrt((cx - rp.x) ** 2 + (cz - rp.z) ** 2)
        if (d < minRoadDist) minRoadDist = d
      }
      if (minRoadDist < pastureR + 3) continue

      if (overlapsAny(cx, cz, pastureDims[0], pastureDims[2], placed, 3)) continue

      const dFromCent = Math.sqrt((cx - centX) ** 2 + (cz - centZ) ** 2)
      const insideScore = 1 - dFromCent / maxR
      const score = insideScore * 30 + Math.min(minRoadDist, 40) * 0.3
      if (score > bestScore) {
        bestScore = score
        bestX = cx
        bestZ = cz
      }
    }

    if (bestScore > 0) {
      structures.push({
        type: 'pasture',
        localX: bestX,
        localZ: bestZ,
        rotation: rng() * Math.PI * 2,
      })
      const pastureMax = Math.max(pastureDims[0], pastureDims[2])
      placed.push({ x: bestX, z: bestZ, w: pastureMax, d: pastureMax })
      pasturesPlaced.push({ x: bestX, z: bestZ })
    }
  }

  // ---------------------------------------------------------------------------
  //  5. OUTLYING STRUCTURES: Farms & Docks (outside the village fence)
  // ---------------------------------------------------------------------------

  // --- Farms: large farmsteads with irregular fenced fields, outside the fence ---
  const farmFootprint = 90 // large footprint for land check + overlap
  for (let fi = 0; fi < config.farms; fi++) {
    let bestX = 0, bestZ = 0, bestScore = -1

    for (let attempt = 0; attempt < 80; attempt++) {
      const angle = rng() * Math.PI * 2
      // Place 1.5–3x the fence radius away
      const dist = maxR * 1.5 + rng() * maxR * 1.5
      const fx = centX + Math.cos(angle) * dist
      const fz = centZ + Math.sin(angle) * dist

      if (overlapsAny(fx, fz, farmFootprint, farmFootprint, placed, 15)) continue

      // Terrain check: ensure farm center is on land (if worldInfo available)
      if (worldInfo) {
        const dir = localToNormal(worldInfo, fx, fz)
        const h = heightAt(dir.nx, dir.ny, dir.nz)
        if (h < 0.006) continue // must be well above water
      }

      // Spread farms apart from each other and from pastures
      let minFarmDist = Infinity
      for (const p of pasturesPlaced) {
        const d = Math.sqrt((fx - p.x) ** 2 + (fz - p.z) ** 2)
        if (d < minFarmDist) minFarmDist = d
      }
      const score = dist + Math.min(minFarmDist, 150) * 0.5
      if (score > bestScore) { bestScore = score; bestX = fx; bestZ = fz }
    }

    if (bestScore > 0) {
      structures.push({
        type: 'farm',
        localX: bestX,
        localZ: bestZ,
        rotation: rng() * Math.PI * 2,
      })
      placed.push({ x: bestX, z: bestZ, w: farmFootprint, d: farmFootprint })
    }
  }

  // ---------------------------------------------------------------------------
  //  6. PROP GENERATION
  // ---------------------------------------------------------------------------
  const props: Prop[] = []

  // Helper: check if prop overlaps any structure (minimum 2m clearance)
  function propOverlaps(px: number, pz: number, minDist: number): boolean {
    for (const p of placed) {
      const d = Math.sqrt((px - p.x) ** 2 + (pz - p.z) ** 2)
      if (d < minDist) return true
    }
    return false
  }

  // Collect structures by type for easy lookup
  const structuresByType = (type: StructureType): Structure[] =>
    structures.filter(s => s.type === type)

  // --- Well (1 per village): near village center, on or near main road ---
  {
    let bestX = 0, bestZ = 0, bestScore = -Infinity
    for (let attempt = 0; attempt < 50; attempt++) {
      // Pick a spot near the center of the main road
      const ni = Math.floor(mainNodes.length * 0.3 + rng() * mainNodes.length * 0.4)
      const node = mainNodes[Math.min(ni, mainNodes.length - 1)]
      // Offset slightly from road center
      const offAngle = rng() * Math.PI * 2
      const offDist = 3 + rng() * 5
      const wx = node.x + Math.cos(offAngle) * offDist
      const wz = node.z + Math.sin(offAngle) * offDist

      if (propOverlaps(wx, wz, 4)) continue

      const dist = Math.sqrt(wx * wx + wz * wz)
      const score = 100 - dist
      if (score > bestScore) {
        bestScore = score
        bestX = wx
        bestZ = wz
      }
    }
    if (bestScore > -Infinity) {
      props.push({ type: 'well', localX: bestX, localZ: bestZ, rotation: rng() * Math.PI * 2 })
    }
  }

  // --- Barrels (2-4): near pub and trading post ---
  {
    const numBarrels = 2 + Math.floor(rng() * 3) // 2-4
    const pubList = structuresByType('pub')
    const tradingList = structuresByType('trading_post')
    const targets = [...pubList, ...pubList, ...tradingList] // weight pub more

    for (let bi = 0; bi < numBarrels; bi++) {
      if (targets.length === 0) break
      const target = targets[bi % targets.length]
      // Place 3-5m from the building, offset to one side
      const sideAngle = target.rotation + (rng() > 0.5 ? Math.PI / 2 : -Math.PI / 2)
      const dist = 3 + rng() * 2
      const bx = target.localX + Math.cos(sideAngle) * dist + (rng() - 0.5) * 1.5
      const bz = target.localZ + Math.sin(sideAngle) * dist + (rng() - 0.5) * 1.5

      if (!propOverlaps(bx, bz, 2)) {
        props.push({ type: 'barrel', localX: bx, localZ: bz, rotation: rng() * Math.PI * 2 })
      }
    }
  }

  // --- Woodpiles (1 per 3-4 houses): against a house wall ---
  {
    const interval = 3 + Math.floor(rng() * 2) // every 3 or 4 houses
    for (let hi = 0; hi < housesPlaced.length; hi += interval) {
      const house = housesPlaced[hi]
      const dims = STRUCTURE_DIMS.house
      // Place against one side wall: offset by half width + 0.5m
      const side = rng() > 0.5 ? 1 : -1
      const perpAngle = house.rotation + side * Math.PI / 2
      const offset = dims[0] / 2 + 0.5
      const wx = house.localX + Math.cos(perpAngle) * offset
      const wz = house.localZ + Math.sin(perpAngle) * offset

      if (!propOverlaps(wx, wz, 1.5)) {
        props.push({ type: 'woodpile', localX: wx, localZ: wz, rotation: house.rotation })
      }
    }
  }

  // --- Haystacks (2-4): near barns and pastures ---
  {
    const numHay = 2 + Math.floor(rng() * 3) // 2-4
    const barnList = structuresByType('barn')
    const targets: Array<{ x: number; z: number }> = [
      ...barnList.map(b => ({ x: b.localX, z: b.localZ })),
      ...pasturesPlaced,
    ]

    for (let hi = 0; hi < numHay; hi++) {
      if (targets.length === 0) break
      const target = targets[hi % targets.length]
      const angle = rng() * Math.PI * 2
      const dist = 10 + rng() * 10 // 10-20m
      const hx = target.x + Math.cos(angle) * dist
      const hz = target.z + Math.sin(angle) * dist

      if (!propOverlaps(hx, hz, 2)) {
        props.push({ type: 'haystack', localX: hx, localZ: hz, rotation: rng() * Math.PI * 2 })
      }
    }
  }

  // --- Carts (1-2): on main road near trading post or barn ---
  {
    const numCarts = 1 + Math.floor(rng() * 2) // 1-2
    const tradingList = structuresByType('trading_post')
    const barnList = structuresByType('barn')
    const targets = [...tradingList, ...barnList]

    for (let ci = 0; ci < numCarts; ci++) {
      if (targets.length === 0) break
      const target = targets[ci % targets.length]
      // Find nearest main road node to target
      let nearestNode = mainNodes[0]
      let nearestDist = Infinity
      for (const node of mainNodes) {
        const d = Math.sqrt((target.localX - node.x) ** 2 + (target.localZ - node.z) ** 2)
        if (d < nearestDist) { nearestDist = d; nearestNode = node }
      }
      // Place slightly off to the side of the road
      const sideAngle = rng() * Math.PI * 2
      const cx = nearestNode.x + Math.cos(sideAngle) * (3 + rng() * 2)
      const cz = nearestNode.z + Math.sin(sideAngle) * (3 + rng() * 2)

      if (!propOverlaps(cx, cz, 2)) {
        // Orient cart roughly along the road
        const roadAngle = Math.atan2(
          mainNodes[Math.min(mainNodes.indexOf(nearestNode) + 1, mainNodes.length - 1)].z - nearestNode.z,
          mainNodes[Math.min(mainNodes.indexOf(nearestNode) + 1, mainNodes.length - 1)].x - nearestNode.x,
        )
        props.push({ type: 'cart', localX: cx, localZ: cz, rotation: roadAngle + (rng() - 0.5) * 0.3 })
      }
    }
  }

  // --- Crates (2-4): near trading post and barn entrances ---
  {
    const numCrates = 2 + Math.floor(rng() * 3) // 2-4
    const tradingList = structuresByType('trading_post')
    const barnList = structuresByType('barn')
    const targets = [...tradingList, ...tradingList, ...barnList] // weight trading post

    for (let ci = 0; ci < numCrates; ci++) {
      if (targets.length === 0) break
      const target = targets[ci % targets.length]
      // Place near entrance (front of building = rotation direction)
      const frontAngle = target.rotation
      const dist = STRUCTURE_DIMS[target.type][2] / 2 + 1 + rng() * 2
      const side = (rng() - 0.5) * 3 // slight side offset
      const cx = target.localX + Math.cos(frontAngle) * dist + Math.cos(frontAngle + Math.PI / 2) * side
      const cz = target.localZ + Math.sin(frontAngle) * dist + Math.sin(frontAngle + Math.PI / 2) * side

      if (!propOverlaps(cx, cz, 1.5)) {
        props.push({ type: 'crate', localX: cx, localZ: cz, rotation: target.rotation + (rng() - 0.5) * 0.5 })
      }
    }
  }

  // --- Troughs (1-2): near stable entrance ---
  {
    const numTroughs = 1 + Math.floor(rng() * 2) // 1-2
    const stableList = structuresByType('stable')

    for (let ti = 0; ti < numTroughs; ti++) {
      if (stableList.length === 0) break
      const stable = stableList[ti % stableList.length]
      // Place near entrance
      const frontAngle = stable.rotation
      const dist = STRUCTURE_DIMS.stable[2] / 2 + 1.5 + rng() * 1.5
      const side = (rng() - 0.5) * 3
      const tx = stable.localX + Math.cos(frontAngle) * dist + Math.cos(frontAngle + Math.PI / 2) * side
      const tz = stable.localZ + Math.sin(frontAngle) * dist + Math.sin(frontAngle + Math.PI / 2) * side

      if (!propOverlaps(tx, tz, 2)) {
        props.push({ type: 'trough', localX: tx, localZ: tz, rotation: stable.rotation })
      }
    }
  }

  return { structures, paths, fencePosts, fenceRadius: maxR, props }
}
